import {
  IntegrationEndpointDetail,
  IntegrationEndpointMinimal,
  IntegrationWebhookMinimal,
} from '@components/integrations/integrations-types';
import { toQueryString } from '@utils/requests';

import { request } from 'api/request';
import { JourneyType } from 'api/types';

export type MarketplaceTemplateAuthText = {
  type: 'text';
  text: string;
};

export type MarketplaceTemplateAuthHtml = {
  type: 'html';
  text: { value: string };
};

export type MarketplaceTemplateAuthListText = {
  type: 'list-text';
  list: string[];
};

export type MarketplaceTemplateAuthListHtml = {
  type: 'list-html';
  list: string[];
};

export type MarketplaceTemplateAuthInfoBox = {
  type: 'info-box';
  text: string;
};

export type MarketplaceTemplateAuthInput = {
  type: 'input';
  label: string;
  field: string;
  helpText?: string;
  placeholder?: string;
};

export type MarketplaceTemplateAuthBaseUrl = {
  type: 'base-url';
  prefix?: string;
  postfix?: string;
  label: string;
  placeholder?: string;
  helpText?: string;
};
export type MarketplaceTemplateAuthInfoItem =
  | MarketplaceTemplateAuthText
  | MarketplaceTemplateAuthHtml
  | MarketplaceTemplateAuthListText
  | MarketplaceTemplateAuthListHtml
  | MarketplaceTemplateAuthInfoBox
  | MarketplaceTemplateAuthInput
  | MarketplaceTemplateAuthBaseUrl;

type MarketplaceTemplateAuthInfo = {
  shortId: string;
  authType: string;
  info?: {
    info: MarketplaceTemplateAuthInfoItem[];
    details: Record<string, any>;
  };
  oauthAuthorizeUrl?: string;
  oauthRedirectUri?: string;
};
export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'credentials_invalid'
  | 'not_connected';

export type MarketplaceTemplate = {
  shortId: string;
  name: string;
  description: string;
  logo: string;
  info: {
    features: string[];
    builtBy: string;
    docs: string;
    website: string;
  };
  connectionStatus: ConnectionStatus;
  connectedIntegrationId?: string;
  validationUrl?: string;
  skipCredentialValidation: boolean;
  connectionDetails?: Record<string, any>;
  authentications: MarketplaceTemplateAuthInfo[];
};

export const connectMarketplaceTemplate = async (params: {
  templateId: string;
  authenticationId: string;
  values: Record<string, any>;
  journeyActionId?: string;
}) => {
  const { templateId, authenticationId, values, journeyActionId } = params;
  const response = await request.post('v1/marketplace/connect/', {
    template_id: templateId,
    authentication_id: authenticationId,
    values,
    ...(journeyActionId && { journey_action_id: journeyActionId }),
  });
  return response.data;
};

export const disconnectIntegration = async (integrationId: string) => {
  const response = await request.patch(`v1/marketplace/disconnect/${integrationId}/`);
  return response.data;
};

export const testIntegrationConnection = async (
  integrationId: string,
  options?: { skipIfRecent?: boolean }
) => {
  const skipIfRecent = options?.skipIfRecent ?? false;
  const response = await request.post<{ success: boolean; status_code: number }>(
    `v1/marketplace/test/${integrationId}/?skip_if_recent=${skipIfRecent}`
  );
  return response.data;
};

export const reconnectIntegration = async (params: {
  integrationId: string;
  values: Record<string, any>;
  journeyActionId?: string;
  authenticationId?: string;
}) => {
  const { integrationId, values, journeyActionId, authenticationId } = params;
  const response = await request.patch(`v1/marketplace/reconnect/${integrationId}/`, {
    values,
    ...(journeyActionId && { journey_action_id: journeyActionId }),
    ...(authenticationId && { authentication_id: authenticationId }),
  });
  return response.data;
};

export const initiateOAuthAuthorize = async (params: {
  authenticationShortId: string;
  journeyActionId?: string;
  values?: Record<string, any>;
}) => {
  const { authenticationShortId, journeyActionId, values } = params;
  const response = await request.post<{ authorizationUrl: string }>(
    `v1/marketplace/oauth/authorize/${authenticationShortId}/`,
    {
      ...(journeyActionId && { journey_action_id: journeyActionId }),
      ...(values && { values }),
    }
  );
  return response.data;
};

export type MarketplaceTemplateFilters = {
  status?: 'all' | 'connected' | 'available';
  search?: string;
};

export const getMarketplaceTemplates = async (filters?: MarketplaceTemplateFilters) => {
  const query = {
    status: filters?.status === 'all' ? undefined : filters?.status,
    search: filters?.search,
  };
  const queryString = toQueryString(query);
  const response = await request.get<MarketplaceTemplate[]>(
    `v1/marketplace/templates/${queryString}`
  );
  return response.data;
};

export const getMarketplaceTemplate = async (shortId: string) => {
  const response = await request.get<MarketplaceTemplate>(`v1/marketplace/templates/${shortId}/`);
  return response.data;
};

export type MarketplaceIntegrationGroup = {
  shortId: string;
  name: string;
  description: string;
  steps: { shortId: string }[];
};

export type MarketplaceIntegration = {
  shortId: string;
  templateId: string;
  name: string;
  description: string;
  logo: string;
  endpoints: IntegrationEndpointMinimal[];
  webhooks: IntegrationWebhookMinimal[];
  groups: MarketplaceIntegrationGroup[];
};

export type InstantiateGroupInput = {
  groupShortId: string;
  journey: string;
  parentAction: string;
  childActions: string[];
};

export type InstantiateGroupResponse = {
  shortIds: string[];
};

export const instantiateMarketplaceGroup = async (data: InstantiateGroupInput) => {
  const { groupShortId, ...body } = data;
  const response = await request.post<InstantiateGroupResponse>(
    `v1/marketplace/groups/${groupShortId}/instantiate/`,
    body
  );
  return response.data;
};
export const getMarketplaceIntegrationsConnected = async () => {
  const response = await request.get<MarketplaceIntegration[]>(`v1/marketplace/connected/`);
  return response.data;
};

export const getMarketplaceIntegrationsNotConnected = async () => {
  const response = await request.get<MarketplaceIntegration[]>(`v1/marketplace/not-connected/`);
  return response.data;
};

export const getTemplateIntegrationJourneys = async (templateId: string) => {
  const response = await request.get<JourneyType[]>(
    `v1/marketplace/templates/${templateId}/journeys/`
  );
  return response.data;
};

export type TemplateEndpointDetail = IntegrationEndpointDetail & {
  templateId: string;
};

export const getTemplateEndpoint = async (shortId: string) => {
  const response = await request.get<TemplateEndpointDetail>(
    `v1/marketplace/template-endpoint/${shortId}/`
  );
  return response.data;
};

export type TemplateWebhookDetail = IntegrationWebhookMinimal & {
  templateId: string;
  conditions: {
    id: string;
    field?: object;
    comparator?: string;
    value?: string;
    visible?: boolean;
  }[];
  integration: string;
  uniqueUrl: string;
  instructions?: string;
};

export const getTemplateWebhook = async (shortId: string) => {
  const response = await request.get<TemplateWebhookDetail>(
    `v1/marketplace/template-webhook/${shortId}/`
  );
  return response.data;
};

export type MarketplaceListOption = {
  value: string;
  label: string;
};

export type MarketplaceList = {
  shortId: string;
  name: string;
  options: MarketplaceListOption[];
  listType: 'manual' | 'synced';
  isSyncActive: boolean;
};

export type MarketplaceIntegrationWithLists = {
  shortId: string;
  name: string;
  logo: string;
  lists: MarketplaceList[];
};

export const getMarketplaceIntegrationLists = async () => {
  const response = await request.get<MarketplaceIntegrationWithLists[]>('v1/marketplace/lists/');
  return response.data;
};
